import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuthStore } from '../store/authStore'

const HomePage: React.FC = () => {
  const { isAuthenticated } = useAuthStore()

  return (
    <div className="relative min-h-screen">
      {/* Hero Section - Mimicking the existing landing page */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-space-cadet-primary via-space-cadet-secondary to-cool-gray overflow-hidden">
        {/* Background Animation Placeholder */}
        <div className="absolute inset-0 opacity-20">
          {/* This would contain the Lottie animation from the existing landing page */}
          <div className="w-full h-full bg-gradient-to-br from-transparent to-pink-lavender/10" />
        </div>

        <div className="relative z-10 text-center text-white px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h1 className="text-serif text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Honor Their
              <br />
              <span className="text-pink-lavender">Legacy</span>
            </h1>

            <p className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto leading-relaxed opacity-90">
              Create meaningful eulogies through compassionate conversation and AI-powered guidance
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              {isAuthenticated ? (
                <Link
                  to="/conversation"
                  className="bg-pink-lavender text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-opacity-90 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105"
                >
                  Create Your Eulogy
                </Link>
              ) : (
                <button className="bg-pink-lavender text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-opacity-90 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105">
                  Start Creating
                </button>
              )}

              <Link
                to="/existing"
                className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-space-cadet-primary transition-all duration-300"
              >
                Use Legacy Interface
              </Link>
            </div>

            <motion.div
              className="mt-12 text-sm opacity-70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.7 }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              <p>Built by a funeral celebrant and software engineer</p>
              <p>to help families during their most difficult moments</p>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.5 }}
        >
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <motion.div
              className="w-1 h-3 bg-white rounded-full mt-2"
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-warm-background">
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-serif text-4xl md:text-5xl font-bold text-space-cadet-primary mb-4">
              Sophisticated Conversational AI
            </h2>
            <p className="text-xl text-cool-gray max-w-3xl mx-auto">
              Our advanced system adapts to your emotional state and relationship to create truly personalized eulogies
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-gradient-blue rounded-lg flex items-center justify-center mb-6">
                  <span className="text-white text-2xl">{feature.icon}</span>
                </div>
                <h3 className="text-serif text-xl font-semibold text-space-cadet-primary mb-4">
                  {feature.title}
                </h3>
                <p className="text-cool-gray">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-space-cadet-primary text-white">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-serif text-4xl md:text-5xl font-bold mb-6">
              Ready to Begin?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto text-cool-gray">
              Start creating a meaningful tribute that truly honors your loved one's memory
            </p>
            {isAuthenticated ? (
              <Link
                to="/conversation"
                className="bg-pink-lavender text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-opacity-90 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 inline-block"
              >
                Begin Your Eulogy
              </Link>
            ) : (
              <button className="bg-pink-lavender text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-opacity-90 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105">
                Get Started Today
              </button>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  )
}

const features = [
  {
    icon: '🧠',
    title: 'Emotional Intelligence',
    description: 'Our AI adapts to your grief stage and emotional state, asking the right questions at the right time.'
  },
  {
    icon: '👨‍👩‍👧‍👦',
    title: 'Family Collaboration',
    description: 'Invite up to 4 family members or friends to contribute memories — 5 people total can collaborate.'
  },
  {
    icon: '📚',
    title: 'Professional Library',
    description: 'Enhanced by 5 years of professional eulogies from our co-founder funeral celebrant.'
  }
]

export default HomePage